<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Orders', path: {name: 'admin-orders'} }]" :name="orderName" no-title/>
    <b-row v-if="!loading">
      <b-col lg="3" md="6">
        <b-card class="admin-order-card">
          <div class="admin-order-card-label">Seller:</div>
          <div class="admin-order-card-title">{{ order.seller_license.facility.name }}</div>

          <div class="admin-order-card-label mt-2">License:</div>
          <div>{{ order.seller_license.number }}</div>

          <div class="mt-2">
            <div class="admin-order-card-label">Pickup Address:</div>
            <div v-if="sellerLicenseAddress.first">
              {{ sellerLicenseAddress.first }}
            </div>
            <div v-if="sellerLicenseAddress.second">
              {{ sellerLicenseAddress.second }}
            </div>
          </div>

          <div class="admin-order-card-label mt-2">Sales Rep:</div>
          <div v-if="order.seller_user">{{ order.seller_user.full_name }}</div>
        </b-card>
      </b-col>
      <b-col lg="4" md="6">
        <b-card class="admin-order-card">
          <div class="admin-order-card-label">Buyer:</div>
          <div class="admin-order-card-title">{{ order.buyer_license.facility.name }}</div>

          <div class="admin-order-card-label mt-2">License:</div>
          <div>{{ order.buyer_license.number }}</div>

          <div class="my-2">
            <div class="admin-order-card-label">Delivery Address:</div>
            <div v-if="buyerLicenseAddress.first">
              {{ buyerLicenseAddress.first }}
            </div>
            <div v-if="buyerLicenseAddress.second">
              {{ buyerLicenseAddress.second }}
            </div>
          </div>

          <div class="row q-col-gutter-sm">
            <div class="col-6">
              <div class="admin-order-card-label">Point of Contact:</div>
              <div>{{ order.buyer_user ? order.buyer_user.full_name : '' }}</div>
              <div v-if="order.buyer_user.phone">
                <a :href="`${buyerPhoneLink}`" class="text-black">{{ order.buyer_user.phone }}</a>
              </div>
              <div>
                <a :href="buyerEmailLink" class="text-black">{{ order.buyer_user.email }}</a>
              </div>
            </div>
            <div class="col-6">
              <div class="admin-order-card-label">Corporate Address:</div>
              <div v-if="buyerLicenseAddress.first">
                {{ buyerLicenseAddress.first }}
              </div>
              <div v-if="buyerLicenseAddress.second">
                {{ buyerLicenseAddress.second }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
      <b-col lg="5" md="6">
        <b-card class="admin-order-card">
          <div class="row q-col-gutter-md">
            <div class="col-6 col-sm-6 col-xs-12">
              <div class="admin-order-card-title">Order</div>
              <div>{{ order.id }}</div>

              <div class="row q-col-gutter-md">
                <div class="col-6">
                  <div class="admin-order-card-label mt-2">Order Type:</div>
                  <div v-if="order.seller_license.type.is_recreational">Recreational</div>
                  <div v-if="order.seller_license.type.is_medical">Medical</div>
                </div>
                <div v-if="order.fee_total" class="col-6">
                  <div class="row" style="align-items: flex-end; flex-wrap: nowrap">
                    <div>
                      <div class="admin-order-card-label mt-2">Order Fees:</div>
                      <div>
                        <span class="mr-2">{{ getFormattedPrice(order.fee_total) }}</span>
                        <admin-order-fee-payment-status class="text-uppercase" :order="order"/>
                      </div>
                    </div>
                    <div v-if="order.line_fees.length">
                      <b-icon-info-circle-fill id="fees-info-icon" style="width: 26px; height: 26px;" variant="primary" class="mx-2"/>
                      <b-tooltip target="fees-info-icon">
                        <ul class="px-2 py-2 mb-0" style="list-style-type: none">
                          <li v-for="(fee, key) in order.line_fees" :key="key">
                            {{ fee.name }}: {{ fee.value }}
                          </li>
                        </ul>
                      </b-tooltip>
                    </div>

                  </div>
                </div>
              </div>

              <div class="admin-order-card-label mt-2">Order Created:</div>
              <div>{{ convertFromUtc(order.created_at, 'M/D/YY h:mm a') }}</div>

              <div class="admin-order-card-label mt-2">Customer:</div>
              <div>{{ order.buyer_license.facility.name }}</div>

              <template v-if="order.notes">
                <div class="admin-order-card-label mt-2" style="margin-bottom: 6px;">
                  <div>Note to Buyer:</div>
                </div>
                <div>{{ order.notes }}</div>
              </template>
            </div>
            <div class="col-6 col-sm-6 col-xs-12">
              <div class="admin-order-card-label mt-2">Payment Terms:</div>
              <b-badge
                  v-for="(pt, index) in order.payment_terms"
                  :key="index"
                  :variant="pt.pivot.is_selected ? 'success' : 'secondary'"
                  class="mr-2 mt-2"
              >
                {{ pt.name }}
              </b-badge>
              <div class="row q-col-gutter-md">
                <div class="col-6">
                  <div class="admin-order-card-label mt-2">Payment Due Date:</div>
                  <div><u>To be Added</u></div>

                  <div class="admin-order-card-label mt-2">Payable to:</div>
                  <div>{{ order.seller_license.facility.name }}</div>
                  <div v-if="sellerLicenseAddress.first">
                    {{ sellerLicenseAddress.first }}
                  </div>
                  <div v-if="sellerLicenseAddress.second">
                    {{ sellerLicenseAddress.second }}
                  </div>
                </div>
                <div class="col-6">
                  <div class="admin-order-card-label mt-2">Amount Due:</div>
                  <div>{{ getFormattedPrice(order.price_total) }}</div>

                  <div class="admin-order-card-label mt-2">Payment Status:</div>
                  <div><u>To be Added</u></div>
                </div>
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row v-if="!loading">
      <b-col size="12">
        <div v-if="hasAcceptedItems" class="my-3 order-items-table-accepted py-2">
          <div v-for="(orderItem, index) in order.items" :key="index">
            <div v-if="orderItem.is_accepted">
              <div class="row no-wrap">
                <admin-order-item-row
                    :order="order"
                    :item="orderItem"
                    :index="index"
                    accepted
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="hasPendingOrDeclinedItems" class="single-order-items mt-4">
          <div v-for="(orderItem, index) in order.items" :key="index">
            <div v-if="!orderItem.is_accepted" class="row no-wrap">
              <admin-order-item-row
                  :order="order"
                  :item="orderItem"
                  :index="orderItem.index"
                  :declined="orderItem.is_declined"
              />
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import {API_ROUTES} from "../api";
import AdminPreloader from "../components/AdminPreloader.vue";
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import {getFormattedPrice, convertFromUtc} from "../helpers";
import AdminOrderFeePaymentStatus from "../components/AdminOrderFeePaymentStatus.vue";
import AdminOrderItemRow from "../components/AdminOrderItemRow.vue";

export default {
  name: "AdminOrder",
  components: {AdminOrderItemRow, AdminOrderFeePaymentStatus, AdminBreadcrumb, AdminPreloader},
  props: {
    id: {
      required: true
    }
  },
  data() {
    return {
      loading: true,
      order: null
    }
  },
  computed: {
    orderName() {
      if (!this.order) {
        return "";
      }
      return `Order # ${this.order.id}`;
    },
    sellerLicenseAddress() {
      let address = {
        first: "",
        second: ""
      };
      if (this.order.seller_license) {
        let location = this.order.seller_license.location;
        address.first = location.address_line_1;
        if (location.address_line_2) {
          address.first += `, ${location.address_line_2}`;
        }
        if (location.address_city) {
          address.second = location.address_city;
        }
        if (location.address_state) {
          if (address.second.length) {
            address.second += ", ";
          }
          address.second += location.address_state;
        }
        if (location.address_zip) {
          if (address.second.length) {
            address.second += " ";
          }
          address.second += location.address_zip;
        }
      }
      return address;
    },
    buyerLicenseAddress() {
      let address = {
        first: "",
        second: ""
      };
      if (this.order.buyer_license) {
        let location = this.order.buyer_license.location;
        address.first = location.address_line_1;
        if (location.address_line_2) {
          address.first += `, ${location.address_line_2}`;
        }
        if (location.address_city) {
          address.second = location.address_city;
        }
        if (location.address_state) {
          if (address.second.length) {
            address.second += ", ";
          }
          address.second += location.address_state;
        }
        if (location.address_zip) {
          if (address.second.length) {
            address.second += " ";
          }
          address.second += location.address_zip;
        }
      }
      return address;
    },
    buyerPhoneLink() {
      let number = this.order.buyer_user.phone ? this.order.buyer_user.phone.replace(/\D/g,'') : '';

      return `tel:+1${number}`;
    },
    buyerEmailLink() {
      return `mailto:${this.order.buyer_user.email}`;
    },
    hasPendingOrDeclinedItems() {
      return this.order.items.findIndex(i => !i.is_accepted) >= 0;
    },
    hasAcceptedItems() {
      return this.order.items.findIndex(i => !!i.is_accepted) >= 0;
    },
  },
  methods: {
    getFormattedPrice,
    convertFromUtc
  },
  created() {
    this.$api.get(`${API_ROUTES.ADMIN_ORDERS}/${this.id}`).then(({data}) => {
      this.order = data.order;
    }).catch(({response: {data}}) => {
      this.$bvToast.toast(data.message ?? 'Invalid Request', {
        variant: 'danger',
        title: 'Error!'
      });
      this.$router.replace({name: "admin-orders"});
    }).finally(() => this.loading = false);
  }
}
</script>
